<!-- TODO: this is a pure "display" component replacing CalendarMonthDisplay.svelte (to be migrated) -->
<script>
  import { 
    generate_calendar_month_sheet_business_local, 
    is_same_day, 
    today_as_local,
    format_date,
    localize_utc_date
  } from '#lib/util.js';
  import "#lib/system-styles/calendar.css";

  let {
    /** @type {number} month index (0-11) */
    month,
    /** @type {number} year */
    year,
    show_weekdays = false,
    show_adjacent_months = true,
    /** @type {(month: number) => void} callback for month selection */
    on_month_select = undefined,
    /** @type {(date: Date) => void} callback for day selection */
    on_day_select = undefined,
    class: css_class = '',
    // Optional per-day override snippets, passed as `day_1` ... `day_31` props
    ...day_snippets
  } = $props();

  let month_sheet = $derived.by(() =>  
    generate_calendar_month_sheet_business_local(month, year, { format: 'date' }).map(date => {
      // Note: The date from generate_calendar_month_sheet_business_local is already in business timezone
      // Get today's date in business timezone
      const today_local = today_as_local();
      const is_today = is_same_day(date, today_local);
      const date_local = localize_utc_date(date);

      // The date from month_sheet is a UTC timestamp that represents a local business date.
      // We need to localize it to get the correct day number in business timezone:
      // e.g. December 30th, 2024 in CET (UTC+1) is stored as December 29th, 2024 23:00:00 UTC
      // localize_utc_date adjusts it to December 30th, 2024 00:00:00 UTC
      // localize_utc_date(date).getUTCDate() will then return "30"
      return {
        date,
        day_number: date_local.getUTCDate().toString(),
        is_today,
        belongs_to_displayed_month: (date_local.getUTCMonth() === month)
      };
    })
  );
</script>

<svelte:element 
  this={on_month_select ? 'button' : 'div'} 
  onclick={on_month_select ? () => on_month_select(month) : undefined}
  role={on_month_select ? 'button' : ''}
  tabindex={on_month_select ? '0' : ''}
> 
  {#if show_weekdays}
    <div class="calendar-header">
      {#each month_sheet.slice(0, 7) as date_object}
        <div class="calendar-day weekday text-caption text-center text-3">{format_date(date_object.date, 'EE')}</div>
      {/each}
    </div>
  {/if}
  <div class="month-sheet month {css_class}">
    {#each month_sheet as { date, day_number, is_today, belongs_to_displayed_month }}
      <svelte:element 
        this={on_day_select ? 'button' : 'div'}
        data-day-of-month="{day_number}"
        class="calendar-day text-caption text-center"
        class:today={is_today}
        class:adjacent-month={!belongs_to_displayed_month}
        class:invisible={!show_adjacent_months && !belongs_to_displayed_month}
        onclick={!on_month_select && on_day_select ? () => on_day_select(date) : undefined}
        role={on_day_select ? 'button' : ''}
        tabindex={on_day_select ? '0' : ''}
      >
        {#if belongs_to_displayed_month && typeof day_snippets[`day_${day_number}`] === 'function'}
          {@render day_snippets[`day_${day_number}`]()}
        {:else}
          {day_number}
        {/if}
      </svelte:element>
    {/each}
  </div>
</svelte:element>

<style lang="postcss">
  .month-sheet, .calendar-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    width: 100%;
  }
  .calendar-day {
    aspect-ratio: 1;
    position: relative;
    min-width: 0;
    font-size: clamp(8px, var(--caption-font-size), 12px);
    @media (max-width: 350px) {
      font-size: 10px;
    }
    line-height: 115%;
    align-content: center; 
    &.today {
      background: radial-gradient(circle, var(--color-control, var(--color-default-emphasis-background)) 70%, transparent 70%);
      filter: drop-shadow(0 0 4px var(--color-default-emphasis-background));
    }
  }
</style>